import React, { Fragment, useState, useEffect } from 'react';
import axios from "axios";
import styles from "./TeacherModal.module.sass";
import Button from '../../Button';
import ModalContent from './ModalContent';
import Spinner from "../../Spinner";
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';

// What each warning code means, for the summary line.
const WARNING_LABELS = {
  small_class: 'unusually small class',
  display_name: 'display name not First Last / Salutation Last',
  display_grade: 'display grade not written out',
  linked_record: 'linked staff record missing or named differently',
};

const TeacherList = ({ schoolId }) => {
  const [matchCount, setMatchCount] = useState({});
  const [data, setData] = useState();

  // Teachers with at least one real warning, and how many of each kind.
  const summarize = (teachers) => {
    const counts = {};
    let flagged = 0;
    teachers.forEach((teacher) => {
      const real = (teacher.warnings || []).filter((w) => w.level === 'warning');
      if (real.length) flagged += 1;
      real.forEach((w) => { counts[w.code] = (counts[w.code] || 0) + 1; });
    });
    return { flagged, counts };
  };

  const confirmPost = (title, message, url, onSuccess) => {
    confirmAlert({
      title,
      message,
      buttons: [
        {
          label: 'Yes',
          onClick: () => {
            setData();

            axios
              .post(url)
              .then((data) => {
                if (onSuccess) onSuccess(data)
                getTeachers()
              })
              .catch(() => {
                getTeachers()
              })
          }
        },
        {
          label: 'No',
          onClick: () => {}
        }
      ]
    });
  }

  const handleUpdateClick = () => {
    confirmPost(
      'Confirm Update',
      "Are you sure? This will try to find matching student records for each teacher.",
      `/api/v1/educators/match_teacher_to_student?school_id=${schoolId}`,
      (data) => setMatchCount(data.data.data)
    )
  }

  const getTeachers = () => {
    axios
      .get(`/api/v1/students/students_per_teacher?school_id=${schoolId}`)
      .then((data) => {
        setData(data.data.data)
      })
  }

  const handleRescanClick = () => {
    confirmPost(
      'Rescan Grades',
      "Recompute each teacher's grade from the students assigned to them this season? Hand-edited grades are left alone.",
      `/api/v1/educators/rescan_grades?school_id=${schoolId}`
    )
  }

  useEffect(() => {
    getTeachers();
  }, [schoolId])

  return (
    <Fragment>
      {!data && <Spinner color="blue" />}
      {data && (
        <>
          <a href={`/schools/${schoolId}/print_teachers`} target="_blank">Print Teacher List</a>{' | '}
          <a href={`/schools/${schoolId}/print_teachers.csv`} target="_blank">Download CSV Teacher List</a>
          <div style={{ margin: '10px 0', display: 'flex', gap: '10px' }}>
            <Button onClick={handleUpdateClick}>
              Match Staff Records
            </Button>

            <Button onClick={handleRescanClick}>
              Rescan Grades
            </Button>
          </div>
          {data.some((teacher) => teacher.needs_review) && (
            <p>
              Highlighted teachers need review — no staff record could be confidently
              matched, so their display name must be entered or linked by hand.
            </p>
          )}
          {matchCount.matched_teachers && (
            <p>
              Matched teachers: {matchCount.matched_teachers}
            </p>
          )}

          {matchCount.unmatched_teachers && (
            <p>
              Unmatched teachers: {matchCount.unmatched_teachers}
            </p>
          )}
          <hr />
          {(() => {
            const { flagged, counts } = summarize(data);
            const detail = Object.keys(counts)
              .map((code) => `${counts[code]} ${WARNING_LABELS[code] || code}`)
              .join('; ');
            return flagged === 0 ? (
              <div className={styles.summaryOk}>
                <strong>Check Teachers:</strong>
                {` nothing flagged for ${data.length} teachers.`}
              </div>
            ) : (
              <div className={styles.summary}>
                <strong>Check Teachers:</strong>
                {` ${flagged} of ${data.length} teachers need a look (yellow rows): ${detail}.`}
              </div>
            );
          })()}
          <ModalContent data={data} schoolId={schoolId} />
        </>
      )}
    </Fragment>
  )
}

export default TeacherList;
